/**
 * The Terminal tab's pane.
 *
 * Upstream hosts shells in the right panel; Forge's terminals lived only in
 * the bottom drawer. This pane hosts the same sessions: `terminalStateStore`
 * stays the one owner of a thread's session list, so a shell opened here is
 * the shell in the drawer, and a session's history follows it across both.
 * The drawer itself stays; it is the docked home for agent-driven shells,
 * while the tab keeps a shell beside the transcript.
 */
import { PlusIcon, SquareSplitHorizontalIcon, SquareTerminalIcon, XIcon } from "lucide-react";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { type ScopedThreadRef } from "@cmmd-forge/contracts";

import { cn } from "~/lib/utils";
import { useStore } from "~/store";
import { createProjectSelectorByRef, createThreadSelectorByRef } from "~/storeSelectors";
import { selectThreadTerminalState, useTerminalStateStore } from "~/terminalStateStore";

import {
  resolvePersistentTerminalCwd,
  resolvePersistentTerminalProjectRef,
  resolvePersistentTerminalRuntimeEnv,
  resolvePersistentTerminalWorktreePath,
} from "../ChatView.logic";
import { TerminalViewport } from "../ThreadTerminalDrawer";
import { resolveThreadTerminalDrawerState } from "../ThreadTerminalDrawer.logic";
import { addTerminalContextToThreadComposer } from "./terminalContextCoordinator";
import { useThreadTerminalSessionControls } from "./useThreadTerminalSessionControls";

export interface ChatTerminalPaneProps {
  readonly threadRef: ScopedThreadRef;
  readonly active: boolean;
  /** Closing the tab when its last session closes, so the pane never sits on a respawned default shell. */
  readonly onCloseSurface: () => void;
}

/**
 * The pane's own height, observed rather than assumed. The xterm fit inside
 * `TerminalViewport` re-runs whenever its `drawerHeight` input moves, so the
 * pane feeds it the real box it was given instead of a number both sides have
 * to agree on.
 */
function usePaneHeight(ref: RefObject<HTMLDivElement | null>): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    /**
     * Reads the element's rounded client height into state.
     */
    const measure = () => setHeight(Math.round(element.clientHeight));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

/**
 * The Terminal tab's pane body: the thread's sessions rendered as xterms in
 * the panel, sharing the drawer's store, launch context, and guarded close.
 */
export function ChatTerminalPane(props: ChatTerminalPaneProps) {
  const threadRef = props.threadRef;
  const threadId = threadRef.threadId;
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const paneHeight = usePaneHeight(bodyRef);

  const terminalState = useTerminalStateStore((state) =>
    selectThreadTerminalState(state.terminalStateByThreadKey, threadRef),
  );
  const serverThread = useStore(
    // zustand compares selector results by identity, which the compiler
    // cannot see, so the selector itself has to be stable.
    useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]),
  );
  const projectRef = resolvePersistentTerminalProjectRef({ serverThread, draftThread: null });
  const project = useStore(
    useMemo(
      () => (projectRef === null ? () => undefined : createProjectSelectorByRef(projectRef)),
      [projectRef],
    ),
  );

  const worktreePath = resolvePersistentTerminalWorktreePath({
    activeLaunchContext: null,
    serverThread,
    draftThread: null,
  });
  const cwd = resolvePersistentTerminalCwd({
    activeLaunchContext: null,
    projectCwd: project?.cwd ?? null,
    worktreePath,
  });
  const runtimeEnv = resolvePersistentTerminalRuntimeEnv({
    activeLaunchContext: null,
    projectCwd: project?.cwd ?? null,
    worktreePath,
  });

  const drawerState = resolveThreadTerminalDrawerState({
    terminalIds: terminalState.terminalIds,
    activeTerminalId: terminalState.activeTerminalId,
    terminalGroups: terminalState.terminalGroups,
    activeTerminalGroupId: terminalState.activeTerminalGroupId,
  });

  const controls = useThreadTerminalSessionControls({
    threadRef,
    threadId,
    sessionIds: terminalState.terminalIds,
    onCloseFinalSession: props.onCloseSurface,
  });

  // Opening the Terminal tab means "give me a shell". A thread's terminal
  // state always carries at least the `default` placeholder id, and attaching
  // `TerminalViewport` to it creates the session (open-or-attach), exactly as
  // the drawer materializes its first shell. The pane adds nothing on top: a
  // deliberate close-everything must not respawn a shell behind the user.
  if (cwd === null) {
    return (
      <div
        className="flex h-full items-center justify-center px-6 text-center text-muted-foreground text-sm"
        data-testid="chat-terminal-pane-empty"
      >
        Connect this thread&apos;s workspace to open a terminal here.
      </div>
    );
  }

  /**
   * Renders one session's viewport inside the pane body.
   */
  const renderViewport = (terminalId: string, autoFocus: boolean) => (
    <TerminalViewport
      key={terminalId}
      threadRef={threadRef}
      threadId={threadId}
      cmmdProjectId={project?.cmmdRegistration?.cmmdProjectId ?? null}
      terminalId={terminalId}
      terminalLabel={drawerState.terminalLabelById.get(terminalId) ?? "Terminal"}
      cwd={cwd}
      worktreePath={worktreePath}
      runtimeEnv={runtimeEnv}
      onSessionExited={() => controls.closeTerminal(terminalId)}
      onAddTerminalContext={addTerminalContextToThreadComposer}
      focusRequestId={0}
      autoFocus={autoFocus}
      resizeEpoch={0}
      drawerHeight={paneHeight > 0 ? paneHeight : 320}
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-background" data-testid="chat-terminal-pane">
      <div className="flex shrink-0 items-center gap-1 border-b border-border/70 px-2 py-1">
        {drawerState.normalizedTerminalIds.map((terminalId) => {
          const isActive = terminalId === drawerState.resolvedActiveTerminalId;
          return (
            <button
              key={terminalId}
              type="button"
              data-testid={`chat-terminal-pane-session-${terminalId}`}
              aria-pressed={isActive}
              onClick={() => controls.activateTerminal(terminalId)}
              className={cn(
                "inline-flex h-6 min-w-0 max-w-40 items-center gap-1 rounded-md px-2 text-xs transition-colors",
                isActive
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
              )}
            >
              <SquareTerminalIcon aria-hidden className="size-3 shrink-0" />
              <span className="truncate">
                {drawerState.terminalLabelById.get(terminalId) ?? "Terminal"}
              </span>
            </button>
          );
        })}
        <span className="flex-1" />
        <button
          type="button"
          aria-label="Split terminal"
          data-testid="chat-terminal-pane-split"
          disabled={drawerState.hasReachedSplitLimit}
          onClick={controls.splitTerminal}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45"
        >
          <SquareSplitHorizontalIcon aria-hidden className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label="New terminal"
          data-testid="chat-terminal-pane-new"
          onClick={controls.createTerminal}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
        >
          <PlusIcon aria-hidden className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label="Close terminal"
          data-testid="chat-terminal-pane-close"
          onClick={() => controls.closeTerminal(drawerState.resolvedActiveTerminalId)}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
        >
          <XIcon aria-hidden className="size-3.5" />
        </button>
      </div>
      <div ref={bodyRef} className="min-h-0 flex-1 p-1">
        {drawerState.isSplitView ? (
          <div
            className="grid h-full w-full min-w-0 gap-0 overflow-hidden"
            style={{
              gridTemplateColumns: `repeat(${drawerState.visibleTerminalIds.length}, minmax(0, 1fr))`,
            }}
          >
            {drawerState.visibleTerminalIds.map((terminalId) => (
              <div
                key={terminalId}
                className={cn(
                  "min-h-0 min-w-0 border-l first:border-l-0",
                  terminalId === drawerState.resolvedActiveTerminalId
                    ? "border-border"
                    : "border-border/70",
                )}
                onMouseDown={() => {
                  if (terminalId !== drawerState.resolvedActiveTerminalId) {
                    controls.activateTerminal(terminalId);
                  }
                }}
              >
                {renderViewport(terminalId, terminalId === drawerState.resolvedActiveTerminalId)}
              </div>
            ))}
          </div>
        ) : (
          renderViewport(drawerState.resolvedActiveTerminalId, props.active)
        )}
      </div>
    </div>
  );
}
